import { Plus, Pencil, Trash2, Loader } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "shared/Button";
import PickPackFormDialog from "./PickPackFormDialog";
import AdditionalServiceFormDialog from "./AdditionalServiceFormDialog";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getPickPackFees,
  addPickPackFee,
  updatePickPackFee,
  deletePickPackFee,
  getAdditionalServices,
  addAdditionalService,
  updateAdditionalService,
  deleteAdditionalService,
} from "core/Services/AdminServices";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  iPickPackFee,
  iAdditionalService,
  PickPackFormData,
  AdditionalServiceFormData,
} from "./PickPackTypes";

const PickPackManagement = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pickPackFees, setPickPackFees] = useState<iPickPackFee[]>([]);
  const [itemServices, setItemServices] = useState<iAdditionalService[]>([]);
  const [orderServices, setOrderServices] = useState<iAdditionalService[]>([]);

  // Modal states
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showItemServiceModal, setShowItemServiceModal] = useState(false);
  const [showOrderServiceModal, setShowOrderServiceModal] = useState(false);

  // Editing states
  const [editingPackage, setEditingPackage] = useState<iPickPackFee | null>(
    null
  );
  const [editingItemService, setEditingItemService] =
    useState<iAdditionalService | null>(null);
  const [editingOrderService, setEditingOrderService] =
    useState<iAdditionalService | null>(null);

  // Deleting states
  const [deletingItemId, setDeletingItemId] = useState<number | null>(null);
  const [deletingType, setDeletingType] = useState<
    "package" | "item" | "order" | null
  >(null);

  // Pick & Pack handlers
  const handleAddPackage = () => {
    setEditingPackage(null);
    setShowPackageModal(true);
  };

  const handleEditPackage = (fee: iPickPackFee) => {
    setEditingPackage(fee);
    setShowPackageModal(true);
  };

  const handleDeletePackage = async (id: number) => {
    try {
      setDeletingItemId(id);
      setDeletingType("package");
      await deletePickPackFee(id);
      await fetchData();
      toast.success(t("pickPackManagement.deleteSuccess"));
    } catch (error) {
      toast.error(t("pickPackManagement.deleteError"));
    } finally {
      setDeletingItemId(null);
      setDeletingType(null);
    }
  };

  const handleSubmitPackage = async (data: PickPackFormData) => {
    try {
      if (editingPackage) {
        await updatePickPackFee(data, editingPackage.id);
        toast.success(t("pickPackManagement.updateSuccess"));
      } else {
        await addPickPackFee(data);
        toast.success(t("pickPackManagement.addSuccess"));
      }
      setShowPackageModal(false);
      await fetchData();
    } catch (error) {
      toast.error(t("pickPackManagement.saveError"));
    }
  };

  // Item Service handlers
  const handleAddItemService = () => {
    setEditingItemService(null);
    setShowItemServiceModal(true);
  };

  const handleEditItemService = (service: iAdditionalService) => {
    setEditingItemService(service);
    setShowItemServiceModal(true);
  };

  const handleDeleteItemService = async (id: number) => {
    try {
      setDeletingItemId(id);
      setDeletingType("item");
      await deleteAdditionalService(id);
      await fetchData();
      toast.success(
        t("pickPackManagement.serviceDeleteSuccess") ||
          "Service deleted successfully!"
      );
    } catch (error) {
      toast.error(t("pickPackManagement.deleteError"));
    } finally {
      setDeletingItemId(null);
      setDeletingType(null);
    }
  };

  const handleSubmitItemService = async (data: AdditionalServiceFormData) => {
    try {
      if (editingItemService) {
        await updateAdditionalService(data, editingItemService.id);
        toast.success(
          t("pickPackManagement.serviceUpdateSuccess") ||
            "Service updated successfully!"
        );
      } else {
        await addAdditionalService(data);
        toast.success(
          t("pickPackManagement.serviceAddSuccess") ||
            "Service added successfully!"
        );
      }
      setShowItemServiceModal(false);
      await fetchData();
    } catch (error) {
      toast.error(t("pickPackManagement.saveError"));
    }
  };

  // Order Service handlers
  const handleAddOrderService = () => {
    setEditingOrderService(null);
    setShowOrderServiceModal(true);
  };

  const handleEditOrderService = (service: iAdditionalService) => {
    setEditingOrderService(service);
    setShowOrderServiceModal(true);
  };

  const handleDeleteOrderService = async (id: number) => {
    try {
      setDeletingItemId(id);
      setDeletingType("order");
      await deleteAdditionalService(id);
      await fetchData();
      toast.success(
        t("pickPackManagement.serviceDeleteSuccess") ||
          "Service deleted successfully!"
      );
    } catch (error) {
      toast.error(t("pickPackManagement.deleteError"));
    } finally {
      setDeletingItemId(null);
      setDeletingType(null);
    }
  };

  const handleSubmitOrderService = async (data: AdditionalServiceFormData) => {
    try {
      if (editingOrderService) {
        await updateAdditionalService(data, editingOrderService.id);
        toast.success(
          t("pickPackManagement.serviceUpdateSuccess") ||
            "Service updated successfully!"
        );
      } else {
        await addAdditionalService(data);
        toast.success(
          t("pickPackManagement.serviceAddSuccess") ||
            "Service added successfully!"
        );
      }
      setShowOrderServiceModal(false);
      await fetchData();
    } catch (error) {
      toast.error(t("pickPackManagement.saveError"));
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [packagesResponse, servicesResponse] = await Promise.all([
        getPickPackFees(),
        getAdditionalServices(),
      ]);
      setPickPackFees(packagesResponse.data);

      // Split services by service_level
      const allServices: iAdditionalService[] = servicesResponse.data;
      setItemServices(
        allServices.filter((s) => s.service_level === "item")
      );
      setOrderServices(
        allServices.filter((s) => s.service_level === "order")
      );
    } catch (error) {
      toast.error(t("pickPackManagement.fetchError"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const TableSkeleton = ({ columns }: { columns: number }) => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              {[...Array(columns)].map((_, index) => (
                <th
                  key={index}
                  className="px-6 py-4 text-left text-sm font-medium text-gray-500"
                >
                  <Skeleton width={80} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {[...Array(3)].map((_, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {[...Array(columns)].map((_, colIndex) => (
                  <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
                    <Skeleton width={colIndex === columns - 1 ? 60 : 100} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-medium text-gray-900 mb-6">
        {t("pickPackManagement.title")}
      </h2>

      {isLoading ? (
        <div className="space-y-8">
          <TableSkeleton columns={6} />
          <TableSkeleton columns={5} />
          <TableSkeleton columns={5} />
        </div>
      ) : (
        <>
          {/* Pick & Pack Fees Section */}
          <div className="mb-8">
            <div className="flex flex-col gap-2 md:flex-row md:justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("pickPackManagement.packagesSection") ||
                  "Pick & Pack Fees"}
              </h3>
              <Button
                onClick={handleAddPackage}
                icon={<Plus size={16} />}
                variant="outlined"
              >
                {t("pickPackManagement.addPackage") || "Add Package"}
              </Button>
            </div>
            {pickPackFees.length === 0 ? (
              <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-md text-yellow-800 text-center">
                {t("pickPackManagement.noPackages") || "No packages found."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.id")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.name")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.description")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.range")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.price")}
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                        {t("pickPackManagement.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pickPackFees.map((fee) => (
                      <tr key={fee.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {fee.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {fee.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {fee.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {fee.min_items}-{fee.max_items || "∞"} items
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {Number(fee.price).toFixed(3)} JOD
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              className="text-blue-600 hover:text-blue-900"
                              onClick={() => handleEditPackage(fee)}
                            >
                              <Pencil className="h-5 w-5" />
                            </button>
                            <button
                              className={`text-red-600 hover:text-red-900 ${
                                deletingItemId === fee.id &&
                                deletingType === "package"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              onClick={() => handleDeletePackage(fee.id)}
                              disabled={
                                deletingItemId === fee.id &&
                                deletingType === "package"
                              }
                            >
                              {deletingItemId === fee.id &&
                              deletingType === "package" ? (
                                <Loader className="h-5 w-5 animate-spin" />
                              ) : (
                                <Trash2 className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Item Services Section */}
          <div className="mb-8">
            <div className="flex flex-col gap-2 md:flex-row md:justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("pickPackManagement.itemServicesSection") ||
                  "Item Services (Per Item)"}
              </h3>
              <Button
                onClick={handleAddItemService}
                icon={<Plus size={16} />}
                variant="outlined"
              >
                {t("pickPackManagement.addItemService") || "Add Item Service"}
              </Button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {t("pickPackManagement.itemServicesDescription") ||
                "Services applied per item (e.g., shrink wrap, bubble wrap)"}
            </p>
            {itemServices.length === 0 ? (
              <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-md text-yellow-800 text-center">
                {t("pickPackManagement.noItemServices") ||
                  "No item services found."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.id")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.name")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.description")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.pricePerItem") || "Price/Item"}
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                        {t("pickPackManagement.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {itemServices.map((service) => (
                      <tr key={service.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {service.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {service.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {service.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {Number(service.price).toFixed(3)} JOD
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              className="text-blue-600 hover:text-blue-900"
                              onClick={() => handleEditItemService(service)}
                            >
                              <Pencil className="h-5 w-5" />
                            </button>
                            <button
                              className={`text-red-600 hover:text-red-900 ${
                                deletingItemId === service.id &&
                                deletingType === "item"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              onClick={() =>
                                handleDeleteItemService(service.id)
                              }
                              disabled={
                                deletingItemId === service.id &&
                                deletingType === "item"
                              }
                            >
                              {deletingItemId === service.id &&
                              deletingType === "item" ? (
                                <Loader className="h-5 w-5 animate-spin" />
                              ) : (
                                <Trash2 className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Order Services Section */}
          <div>
            <div className="flex flex-col gap-2 md:flex-row md:justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {t("pickPackManagement.orderServicesSection") ||
                  "Order Services (Per Order)"}
              </h3>
              <Button
                onClick={handleAddOrderService}
                icon={<Plus size={16} />}
                variant="outlined"
              >
                {t("pickPackManagement.addOrderService") || "Add Order Service"}
              </Button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {t("pickPackManagement.orderServicesDescription") ||
                "Services applied per order (e.g., box packaging, gift wrapping)"}
            </p>
            {orderServices.length === 0 ? (
              <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-md text-yellow-800 text-center">
                {t("pickPackManagement.noOrderServices") ||
                  "No order services found."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.id")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.name")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.description")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                        {t("pickPackManagement.pricePerOrder") || "Price/Order"}
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                        {t("pickPackManagement.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderServices.map((service) => (
                      <tr key={service.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {service.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {service.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {service.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {Number(service.price).toFixed(3)} JOD
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              className="text-blue-600 hover:text-blue-900"
                              onClick={() => handleEditOrderService(service)}
                            >
                              <Pencil className="h-5 w-5" />
                            </button>
                            <button
                              className={`text-red-600 hover:text-red-900 ${
                                deletingItemId === service.id &&
                                deletingType === "order"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              onClick={() =>
                                handleDeleteOrderService(service.id)
                              }
                              disabled={
                                deletingItemId === service.id &&
                                deletingType === "order"
                              }
                            >
                              {deletingItemId === service.id &&
                              deletingType === "order" ? (
                                <Loader className="h-5 w-5 animate-spin" />
                              ) : (
                                <Trash2 className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Pick & Pack Form Dialog */}
      {showPackageModal && (
        <PickPackFormDialog
          isOpen={showPackageModal}
          onClose={() => setShowPackageModal(false)}
          onSubmit={handleSubmitPackage}
          initialData={editingPackage}
        />
      )}

      {/* Item Service Form Dialog */}
      {showItemServiceModal && (
        <AdditionalServiceFormDialog
          isOpen={showItemServiceModal}
          onClose={() => setShowItemServiceModal(false)}
          onSubmit={handleSubmitItemService}
          initialData={editingItemService}
          mode={editingItemService ? "edit" : "add"}
          serviceLevel="item"
        />
      )}

      {/* Order Service Form Dialog */}
      {showOrderServiceModal && (
        <AdditionalServiceFormDialog
          isOpen={showOrderServiceModal}
          onClose={() => setShowOrderServiceModal(false)}
          onSubmit={handleSubmitOrderService}
          initialData={editingOrderService}
          mode={editingOrderService ? "edit" : "add"}
          serviceLevel="order"
        />
      )}
    </div>
  );
};

export default PickPackManagement;
